import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPaperPlane, faCheckCircle, faExclamationCircle, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { faViber, faWhatsapp, faTelegram } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import classes from './ServiceRequestModal.module.css';

const SERVICE_TYPES = [
  { value: 'repair', label: 'Επιδιόρθωση/Μεταποίηση' },
  { value: 'measurement', label: 'Μέτρηση' },
  { value: 'offer', label: 'Προσφορά' },
  { value: 'installation', label: 'Τοποθέτηση' },
  { value: 'other', label: 'Άλλο' },
];

const CONTACT_METHODS = [
  { value: 'viber', label: 'Viber', icon: faViber },
  { value: 'whatsapp', label: 'WhatsApp', icon: faWhatsapp },
  { value: 'telegram', label: 'Telegram', icon: faTelegram },
  { value: 'phone', label: 'Τηλέφωνο', icon: null },
];

function ServiceRequestModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    type: '',
    full_name: '',
    phone: '',
    contact_methods: [],
    email: '',
    area: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContactMethodChange = (method) => {
    setFormData(prev => {
      const methods = prev.contact_methods.includes(method)
        ? prev.contact_methods.filter(m => m !== method)
        : [...prev.contact_methods, method];
      
      // If email is unchecked, clear the email field
      if (method === 'email' && prev.contact_methods.includes('email')) {
        return { ...prev, contact_methods: methods, email: '' };
      }
      
      return { ...prev, contact_methods: methods };
    });
  };

  const resetForm = () => {
    setFormData({
      type: '',
      full_name: '',
      phone: '',
      contact_methods: [],
      email: '',
      area: '',
      description: '',
    });
    setSubmitStatus(null);
    setErrorMessage('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.type || !formData.full_name || !formData.phone || formData.contact_methods.length === 0) {
      setSubmitStatus('error');
      setErrorMessage('Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία.');
      return;
    }

    // Validate email if email contact method is selected
    if (formData.contact_methods.includes('email') && !formData.email) {
      setSubmitStatus('error');
      setErrorMessage('Παρακαλώ συμπληρώστε τη διεύθυνση email.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Build the payload - only include email if it has a value
      const payload = {
        type: formData.type,
        full_name: formData.full_name,
        phone: formData.phone,
        contact_methods: formData.contact_methods,
        area: formData.area || null,
        description: formData.description || null,
      };
      
      // Add email only if the email contact method is selected
      if (formData.contact_methods.includes('email') && formData.email) {
        payload.email = formData.email;
      }

      await axios.post('https://new.api.orders.e-interior.gr/api/service-requests', payload);
      setSubmitStatus('success');
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting service request:', error);
      setSubmitStatus('error');
      setErrorMessage('Υπήρξε πρόβλημα κατά την υποβολή. Παρακαλώ δοκιμάστε ξανά.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEmailSelected = formData.contact_methods.includes('email');

  if (!isOpen) return null;

  return (
    <div className={classes.overlay} onClick={handleClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <button className={classes.closeButton} onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className={classes.header}>
          <h2>Υποβολή Αιτήματος</h2>
          <p>Συμπληρώστε τη φόρμα και θα επικοινωνήσουμε μαζί σας άμεσα!</p>
        </div>

        {submitStatus === 'success' ? (
          <div className={classes.successMessage}>
            <FontAwesomeIcon icon={faCheckCircle} />
            <h3>Το αίτημά σας υποβλήθηκε επιτυχώς!</h3>
            <p>Θα επικοινωνήσουμε μαζί σας σύντομα.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={classes.form}>
            {submitStatus === 'error' && (
              <div className={classes.errorMessage}>
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className={classes.formGroup}>
              <label htmlFor="type">Τύπος Αιτήματος *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
              >
                <option value="">Επιλέξτε τύπο...</option>
                {SERVICE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="full_name">Ονοματεπώνυμο *</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder="Το ονοματεπώνυμό σας"
                required
              />
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="phone">Τηλέφωνο Επικοινωνίας *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="π.χ. 6912345678"
                required
              />
            </div>

            <div className={classes.formGroup}>
              <label>Τρόπος Επικοινωνίας *</label>
              <div className={classes.contactMethods}>
                {CONTACT_METHODS.map(method => (
                  <label key={method.value} className={classes.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.contact_methods.includes(method.value)}
                      onChange={() => handleContactMethodChange(method.value)}
                    />
                    <span className={classes.checkboxCustom}>
                      {method.icon && <FontAwesomeIcon icon={method.icon} />}
                      {method.label}
                    </span>
                  </label>
                ))}
                {/* Email - Full width option */}
                <label className={`${classes.checkboxLabel} ${classes.emailOption}`}>
                  <input
                    type="checkbox"
                    checked={isEmailSelected}
                    onChange={() => handleContactMethodChange('email')}
                  />
                  <span className={classes.checkboxCustom}>
                    <FontAwesomeIcon icon={faEnvelope} />
                    Email
                  </span>
                </label>
              </div>
              
              {/* Email input - shown when email is selected */}
              {isEmailSelected && (
                <div className={classes.emailInputWrapper}>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Εισάγετε τη διεύθυνση email σας"
                    className={classes.emailInput}
                    required
                  />
                </div>
              )}
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="area">Πόλη/Περιοχή</label>
              <input
                type="text"
                id="area"
                name="area"
                value={formData.area}
                onChange={handleInputChange}
                placeholder="π.χ. Αθήνα, Θεσσαλονίκη"
              />
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="description">Περιγραφή Αιτήματος</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Περιγράψτε το αίτημά σας..."
                rows={4}
              />
            </div>

            <button 
              type="submit" 
              className={classes.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className={classes.spinner}></span>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} />
                  ΥΠΟΒΟΛΗ
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ServiceRequestModal;
